import { useEffect, useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  LogOut,
  Filter,
  Phone,
  Mail,
  Users,
  Calendar,
  Trash2,
  X,
  RefreshCw,
  Loader2,
  CheckCircle2,
  Inbox,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TRIPS } from "@/data/trips";
import SEO from "@/components/SEO";
import ClickStatsPanel from "@/components/ClickStatsPanel";
import ActivityChart from "@/components/ActivityChart";

const STATUS_OPTIONS = [
  { value: "nuevo", label: "Nuevo", className: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "contactado", label: "Contactado", className: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "interesado", label: "Interesado", className: "bg-violet-100 text-violet-800 border-violet-200" },
  { value: "reservado", label: "Reservado", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { value: "descartado", label: "Descartado", className: "bg-stone-100 text-stone-600 border-stone-200" },
];

function StatusBadge({ value }) {
  const opt = STATUS_OPTIONS.find((s) => s.value === value) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${opt.className}`}>
      {opt.label}
    </span>
  );
}

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ status: "", trip_slug: "" });
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.trip_slug) params.trip_slug = filters.trip_slug;
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/leads", { params }),
        api.get("/leads/stats/summary"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error cargando datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.trip_slug]);

  const openLead = (lead) => {
    setSelected(lead);
    setNotes(lead.notes || "");
  };

  const updateStatus = async (id, status) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, { status });
      setLeads((arr) => arr.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      // refresh stats
      const { data: s } = await api.get("/leads/stats/summary");
      setStats(s);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error actualizando");
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${selected.id}`, { notes });
      setLeads((arr) => arr.map((l) => (l.id === selected.id ? data : l)));
      setSelected(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este lead? No se podrá recuperar.")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((arr) => arr.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      const { data: s } = await api.get("/leads/stats/summary");
      setStats(s);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const tripBySlug = useMemo(() => Object.fromEntries(TRIPS.map((t) => [t.slug, t])), []);

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink">
      <SEO title="Panel CRM · Club Amistad" description="Mini-CRM Club Amistad" path="/admin" noindex />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-brand-line">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="Club Amistad — Inicio">
            <img src="/club-amistad-logo.png" alt="Club Amistad" className="h-11 w-auto" />
            <span className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-full bg-brand-primary/8 text-brand-primary text-[10px] uppercase tracking-wider font-medium">
              Mini-CRM
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={loadAll} className="btn-ghost !py-2 !px-4" data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" /> Actualizar
            </button>
            <span className="hidden md:inline text-sm text-brand-muted">{user?.email}</span>
            <button onClick={logout} className="btn-primary !py-2 !px-4" data-testid="logout-btn">
              <LogOut className="h-4 w-4" /> Salir
            </button>
          </div>
        </div>
        {/* Subnav */}
        <div className="max-w-7xl mx-auto px-6 pb-2 flex gap-1">
          <NavLink
            to="/admin"
            end
            className={({ isActive }) =>
              `px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
                isActive
                  ? "border-brand-primary text-brand-primary font-medium"
                  : "border-transparent text-brand-muted hover:text-brand-primary"
              }`
            }
            data-testid="nav-leads"
          >
            Leads
          </NavLink>
          <NavLink
            to="/admin/configuracion"
            className={({ isActive }) =>
              `px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
                isActive
                  ? "border-brand-primary text-brand-primary font-medium"
                  : "border-transparent text-brand-muted hover:text-brand-primary"
              }`
            }
            data-testid="nav-settings"
          >
            Configuración
          </NavLink>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-10">
          <p className="overline">Panel administrativo</p>
          <h1 className="font-outfit text-3xl md:text-4xl font-semibold text-brand-primary mt-3">
            Leads recibidos
          </h1>
          <p className="text-brand-muted mt-2 max-w-xl">
            Aquí aparecen las familias que han contactado a través de la web. Gestiona su estado, añade
            notas y haz seguimiento.
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-10" data-testid="stats-grid">
            <div className="card-soft p-5">
              <p className="text-xs uppercase tracking-wider text-brand-muted">Total</p>
              <p className="font-outfit text-3xl font-semibold text-brand-primary mt-1">{stats.total}</p>
            </div>
            {STATUS_OPTIONS.map((s) => (
              <div key={s.value} className="card-soft p-5">
                <p className="text-xs uppercase tracking-wider text-brand-muted">{s.label}</p>
                <p className="font-outfit text-3xl font-semibold text-brand-primary mt-1">
                  {stats.by_status?.[s.value] ?? 0}
                </p>
              </div>
            ))}
          </div>
        )}

        <ClickStatsPanel />

        <ActivityChart />

        {/* Filters */}
        <div className="card-soft p-4 md:p-5 mb-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-brand-muted">
            <Filter className="h-4 w-4" /> Filtrar
          </div>
          <select
            className="field-input !py-2 !w-auto"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            data-testid="filter-status"
          >
            <option value="">Todos los estados</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <select
            className="field-input !py-2 !w-auto"
            value={filters.trip_slug}
            onChange={(e) => setFilters((f) => ({ ...f, trip_slug: e.target.value }))}
            data-testid="filter-trip"
          >
            <option value="">Todos los viajes</option>
            {TRIPS.map((t) => (
              <option key={t.slug} value={t.slug}>{t.name}</option>
            ))}
          </select>
          {(filters.status || filters.trip_slug) && (
            <button
              onClick={() => setFilters({ status: "", trip_slug: "" })}
              className="text-sm text-brand-accent hover:underline"
              data-testid="clear-filters"
            >
              Limpiar filtros
            </button>
          )}
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="dashboard-error">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="card-soft overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
            </div>
          ) : leads.length === 0 ? (
            <div className="py-20 text-center text-brand-muted" data-testid="empty-leads">
              <Inbox className="h-10 w-10 mx-auto mb-4 text-brand-muted/60" />
              <p>Aún no hay leads que coincidan con los filtros.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead className="bg-brand-surface/50 text-brand-muted text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-4 font-medium">Familia</th>
                    <th className="text-left px-5 py-4 font-medium">Viaje</th>
                    <th className="text-left px-5 py-4 font-medium">Contacto</th>
                    <th className="text-left px-5 py-4 font-medium">Personas</th>
                    <th className="text-left px-5 py-4 font-medium">Recibido</th>
                    <th className="text-left px-5 py-4 font-medium">Estado</th>
                    <th className="text-right px-5 py-4 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="border-t border-brand-line hover:bg-brand-surface/30 transition-colors cursor-pointer"
                      onClick={() => openLead(lead)}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <td className="px-5 py-4">
                        <p className="font-medium text-brand-primary">{lead.name}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-brand-ink">
                          {(lead.trip_names && lead.trip_names.length ? lead.trip_names : [tripBySlug[lead.trip_slug]?.name || lead.trip_name]).join(", ")}
                        </p>
                        {(!lead.trip_names || lead.trip_names.length <= 1) && (
                          <p className="text-xs text-brand-muted mt-0.5">{tripBySlug[lead.trip_slug]?.dates_short}</p>
                        )}
                        {lead.trip_names && lead.trip_names.length > 1 && (
                          <p className="text-xs text-brand-muted mt-0.5">{lead.trip_names.length} viajes de interés</p>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-brand-ink">{lead.email}</p>
                        <p className="text-xs text-brand-muted mt-0.5">{lead.phone}</p>
                      </td>
                      <td className="px-5 py-4 text-brand-ink">
                        {lead.adults}A + {lead.children}N
                      </td>
                      <td className="px-5 py-4 text-brand-muted text-xs">
                        {new Date(lead.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge value={lead.status} />
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); remove(lead.id); }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-brand-muted hover:text-red-600 hover:bg-red-50 transition-colors"
                          aria-label="Eliminar"
                          data-testid={`delete-lead-${lead.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Lead Drawer */}
      {selected && (
        <div className="fixed inset-0 z-40" data-testid="lead-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} />
          <aside className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-brand-line px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-brand-muted">Lead</p>
                <p className="font-outfit text-xl text-brand-primary font-semibold">{selected.name}</p>
              </div>
              <button onClick={() => setSelected(null)} className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-brand-surface" data-testid="close-drawer">
                <X className="h-5 w-5 text-brand-muted" />
              </button>
            </div>

            <div className="px-6 py-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Info icon={Mail} label="Email" value={selected.email} />
                <Info icon={Phone} label="Teléfono" value={selected.phone} />
                <Info icon={Users} label="Personas" value={`${selected.adults} adultos + ${selected.children} niños`} />
                <Info icon={Calendar} label="Fechas" value={selected.dates || "—"} />
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-brand-muted">Edades de niños</p>
                <p className="mt-1 font-manrope">{selected.children_ages || "—"}</p>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-brand-muted">
                  {selected.trip_names && selected.trip_names.length > 1 ? "Viajes de interés" : "Viaje de interés"}
                </p>
                {selected.trip_names && selected.trip_names.length ? (
                  <ul className="mt-1 space-y-0.5">
                    {selected.trip_names.map((n, i) => (
                      <li key={i} className="font-manrope">
                        {n}
                        {tripBySlug[selected.trip_slugs?.[i]]?.dates_short && (
                          <span className="text-sm text-brand-muted"> · {tripBySlug[selected.trip_slugs[i]].dates_short}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <>
                    <p className="mt-1 font-manrope">{tripBySlug[selected.trip_slug]?.name || selected.trip_name}</p>
                    <p className="text-sm text-brand-muted">{tripBySlug[selected.trip_slug]?.dates_short}</p>
                  </>
                )}
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-brand-muted">Mensaje</p>
                <p className="mt-1 font-manrope whitespace-pre-wrap text-brand-ink/85">{selected.message || "—"}</p>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-brand-muted mb-2">Cambiar estado</p>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => updateStatus(selected.id, s.value)}
                      disabled={saving || selected.status === s.value}
                      data-testid={`set-status-${s.value}`}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                        selected.status === s.value
                          ? `${s.className} ring-2 ring-offset-1 ring-brand-primary/30 cursor-default`
                          : `${s.className} opacity-70 hover:opacity-100`
                      }`}
                    >
                      {selected.status === s.value && <CheckCircle2 className="h-3 w-3" />}
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="field-label">Notas internas</label>
                <textarea
                  rows={5}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="field-input resize-none"
                  placeholder="Añade información de la llamada, próximos pasos, etc."
                  data-testid="lead-notes"
                />
                <button
                  onClick={saveNotes}
                  disabled={saving}
                  data-testid="save-notes-btn"
                  className="btn-primary mt-3 !py-2.5"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {saving ? "Guardando…" : "Guardar notas"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-brand-muted flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      <p className="mt-1 font-manrope break-words">{value}</p>
    </div>
  );
}
